// EmailAddressItem.jsx
// Shared email-address chip with a hover/tap popover offering Email (opens the
// in-app React SendEmail modal), Mail app (mailto:) and Copy actions. Mirrors
// PhoneNumberItem so phone and email displays stay uniform.
//
// PROTOTYPE: currently only wired on the customer Overview "Email List". Once
// approved, reuse this everywhere emails are listed.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverBody } from "reactstrap";
import { toast } from "react-toastify";

/* cross-instance coordinator (separate from PhoneNumberItem's) */
let ACTIVE_ANCHOR_ID = null;
let SUPPRESS_UNTIL_MS = 0;
const now = () => Date.now();

const Chip = ({ as = "a", href, onClick, icon, children }) => {
  const Tag = as;
  return (
    <Tag
      href={href}
      onClick={onClick}
      rel="noreferrer"
      style={{
        fontSize: 12,
        lineHeight: 1,
        padding: "6px 10px",
        borderRadius: 9999,
        border: "1px solid rgba(0,0,0,.12)",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
        background: "#fff",
        cursor: "pointer",
      }}
    >
      <i className={icon} />
      {children}
    </Tag>
  );
};

/* Routing pills (Task 24323) — which documents get emailed to this address.
   Driven by the Flag* columns on ContactPersonEmailAddress, which the contact
   person API already returns. Opt-in: pass `flags` to show them. */
const ROUTING_PILLS = [
  {
    key: "FlagEmailInvoiceTo",
    label: "Inv To",
    title: "Invoices are emailed TO this address",
    className: "bg-primary-subtle text-primary",
  },
  {
    key: "FlagEmailInvoiceCc",
    label: "Inv CC",
    title: "Invoices are CC'd to this address",
    className: "bg-primary-subtle text-primary",
  },
  {
    key: "FlagEmailProformaInvoiceTo",
    label: "Pro To",
    title: "Proforma invoices are emailed TO this address",
    className: "bg-info-subtle text-info",
  },
  {
    key: "FlagEmailProformaInvoiceCc",
    label: "Pro CC",
    title: "Proforma invoices are CC'd to this address",
    className: "bg-info-subtle text-info",
  },
];

const isFlagOn = (value) => value === 1 || value === "1" || value === true;

export default function EmailAddressItem({
  email,
  isDefault,
  idx,
  ownerId,
  onCompose,
  flags,
}) {
  const [isTouch, setIsTouch] = useState(false);
  const [open, setOpen] = useState(false);

  const closeTimer = useRef(null);
  const openTimer = useRef(null);

  const anchorId = useMemo(
    () =>
      `email-anchor-${ownerId}-${idx}-${(email || "").replace(
        /[^\dA-Za-z]/g,
        ""
      )}`,
    [ownerId, idx, email]
  );

  const mailtoHref = `mailto:${email || ""}`;

  useEffect(() => {
    const touch =
      "ontouchstart" in window ||
      (navigator?.maxTouchPoints ?? 0) > 0 ||
      window.matchMedia?.("(hover: none)")?.matches;
    setIsTouch(!!touch);
  }, []);

  const onCopy = async (e) => {
    e?.preventDefault?.();
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Copied email to clipboard");
    } catch {
      toast.success("Copied");
    }
  };

  const onComposeClick = (e) => {
    e?.preventDefault?.();
    onCompose?.(email);
    closeSoon(0);
  };

  // open/close with grace so you can travel into the popover
  const openAfterDelay = (delay = 140) => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (
      now() < SUPPRESS_UNTIL_MS &&
      ACTIVE_ANCHOR_ID &&
      ACTIVE_ANCHOR_ID !== anchorId
    )
      return;
    openTimer.current = setTimeout(() => {
      if (
        ACTIVE_ANCHOR_ID &&
        ACTIVE_ANCHOR_ID !== anchorId &&
        now() < SUPPRESS_UNTIL_MS
      )
        return;
      if (closeTimer.current) clearTimeout(closeTimer.current);
      ACTIVE_ANCHOR_ID = anchorId;
      setOpen(true);
    }, delay);
  };

  const closeSoon = (delay = 220) => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      if (ACTIVE_ANCHOR_ID === anchorId) ACTIVE_ANCHOR_ID = null;
      setOpen(false);
      SUPPRESS_UNTIL_MS = now() + 180;
    }, delay);
  };

  const openNow = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    ACTIVE_ANCHOR_ID = anchorId;
    setOpen(true);
  };

  const anchorHandlers = isTouch
    ? {
        onClick: (e) => {
          e.preventDefault();
          open ? closeSoon(0) : openNow();
        },
      }
    : {
        onMouseEnter: () => openAfterDelay(140),
        onMouseLeave: () => closeSoon(220),
        onFocus: openNow,
        onBlur: () => closeSoon(150),
      };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        marginBottom: 8,
      }}
    >
      {isDefault && (
        <i
          className="fa-solid fa-star me-1 text-warning"
          title="Default"
          style={{ marginRight: 4 }}
        />
      )}

      <a
        id={anchorId}
        href={mailtoHref}
        {...anchorHandlers}
        style={{
          color: "#212529",
          textDecoration: "none",
          marginLeft: 0,
        }}
      >
        {email}
      </a>

      {flags
        ? ROUTING_PILLS.filter((pill) => isFlagOn(flags[pill.key])).map(
            (pill) => (
              <span
                key={pill.key}
                className={`badge ${pill.className} ms-1`}
                title={pill.title}
                style={{ fontSize: 10, fontWeight: 600 }}
              >
                {pill.label}
              </span>
            )
          )
        : null}

      <Popover
        isOpen={open}
        target={anchorId}
        toggle={() => (open ? closeSoon(0) : openNow())}
        placement="top"
        trigger="manual"
        fade={false}
        container="body"
        popperStyle={{
          maxWidth: "none",
          width: "max-content",
          zIndex: 1085,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,.12)",
        }}
        modifiers={[
          { name: "offset", options: { offset: [0, 10] } },
          { name: "preventOverflow", options: { boundary: "viewport" } },
        ]}
      >
        <PopoverBody
          onMouseEnter={() => !isTouch && openNow()}
          onMouseLeave={() => !isTouch && closeSoon(200)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
            padding: "10px 12px",
            background: "#fff",
            borderRadius: 12,
          }}
        >
          {onCompose && (
            <Chip as="button" onClick={onComposeClick} icon="ri-mail-send-line">
              Email
            </Chip>
          )}
          <Chip href={mailtoHref} icon="ri-mail-line">
            Mail app
          </Chip>
          <Chip as="button" onClick={onCopy} icon="ri-file-copy-2-line">
            Copy
          </Chip>
        </PopoverBody>
      </Popover>
    </div>
  );
}
